import { SetStateAction, useEffect } from 'react'

import { currentLocationIsBranded } from 'branding/branding'
import config from 'config'
import {
    FeatureFlagContextInterface,
    useFeatureFlagContext,
} from 'contexts/FeatureFlagContextProvider'
import useUser from 'hooks/useUser'

/**
 * Determines if an url is fcb by investigating the url.
 * @param url The url to check.
 * @returns True for fcb, false otherwise.
 */
function urlIsFcb(url: string) {
    return config.ENVIRONMENT !== 'local'
        ? url?.includes('fcb')
        : url?.includes('7001')
}

/**
 * Determines if the current location is on a specified url.
 * @param url The url to check.
 * @returns True if on url, false otherwise.
 */
function notOnLocation(url: string) {
    return window.location.origin !== url
}
/**
 * Redirect to url.
 * @param url The url to redirect to.
 */
function redirectToLocation(url: string) {
    window.location.replace(url)
}

/**
 * Determine if redirect should be executed.
 * @param url The request URL.
 * @returns {boolean | undefined} True or false depending on if a redirect should be made.
 *          Returns undefined if state could not be determined.
 */
export function shouldDoRedirect(url: string | undefined): boolean | undefined {
    if (config.ENVIRONMENT === 'e2e' || config.ENVIRONMENT === 'qa') {
        // Branding not supported yet
        return false
    }

    if (!url) {
        return undefined
    }

    if (urlIsFcb(url) && !currentLocationIsBranded()) {
        return false
    }

    return notOnLocation(url)
}

/**
 * Determine if redirect should be executed. Only intended as an internal helper.
 * @param url The request URL.
 * @param featureFlags The feature flag object.
 * @returns True if a redirect should happen, false otherwise. Undefined if either
 * the url or the feature flags states not available.
 */
export function shouldDoRedirectInternal(
    url: string | undefined,
    featureFlags: FeatureFlagContextInterface | null,
) {
    if (config.ENVIRONMENT === 'e2e' || config.ENVIRONMENT === 'qa') {
        // Branding not supported yet
        return false
    }

    if (!url) {
        return undefined
    }

    if (
        featureFlags?.rideNowWhiteLabelRedirect === undefined ||
        featureFlags?.whiteLabelRedirect === undefined
    ) {
        return undefined
    }

    if (urlIsFcb(url) && !currentLocationIsBranded()) {
        return false
    }

    // If both whitelabel flags disabled, only redirect if on branded portal
    if (
        !featureFlags?.rideNowWhiteLabelRedirect &&
        !featureFlags?.whiteLabelRedirect &&
        !currentLocationIsBranded()
    ) {
        return false
    }

    return notOnLocation(url)
}

interface DealerRedirectorInterface {
    onRedirectStateChange: (
        redirect: SetStateAction<boolean | undefined>,
    ) => void
}

export default function DealerRedirector({
    onRedirectStateChange,
}: DealerRedirectorInterface) {
    const { user } = useUser()
    const featureFlags = useFeatureFlagContext()

    useEffect(() => {
        onRedirectStateChange(undefined)

        const shouldRedirect = shouldDoRedirectInternal(
            user?.dealerPortalUrl,
            featureFlags,
        )

        if (shouldRedirect === undefined) {
            return
        }

        if (shouldRedirect) {
            onRedirectStateChange(true)

            redirectToLocation(
                `${user?.dealerPortalUrl}${window.location.hash}`,
            )

            return
        }

        onRedirectStateChange(false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        user?.dealerPortalUrl,
        featureFlags?.whiteLabelRedirect,
        featureFlags?.rideNowWhiteLabelRedirect,
    ])

    return null
}
